import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Link, Stack, Typography } from '@mui/material';
import {
  CheckCircleOutline as AnsweredIcon,
  RadioButtonUnchecked as UnansweredIcon,
  TaskAltOutlined as ReadyIcon,
} from '@mui/icons-material';
import { JobQuestion, JobQuestionKind, JobQuestions } from '../../types';

interface Props {
  questions: JobQuestions;
  /** h2 on the job's own page, h3 inside the drawer. */
  headingComponent: 'h2' | 'h3';
}

/** Show this many rows before "show all"; missing required ones always show. */
const MAX_SHOWN = 8;

// What a question is, when its label does not say. Standard and custom
// questions need no note.
const KIND_LABELS: Partial<Record<JobQuestionKind, string>> = {
  eeo: 'Voluntary self-identification',
  file: 'Attachment',
};

const isMissing = (q: JobQuestion): boolean => q.required && !q.answered;

/**
 * What the employer's form asks and which of it the user has answered. The
 * required questions still unanswered come first, since those are what stand
 * between the user and "ready to apply".
 */
const ApplicationQuestions: React.FC<Props> = ({ questions, headingComponent }) => {
  const [showAll, setShowAll] = useState(false);

  const ordered = useMemo(() => {
    const rows = questions.questions ?? [];
    return [...rows.filter(isMissing), ...rows.filter((q) => !isMissing(q))];
  }, [questions]);
  const missingCount = ordered.filter(isMissing).length;
  const visible = showAll ? ordered : ordered.slice(0, Math.max(MAX_SHOWN, missingCount));
  const hidden = ordered.length - visible.length;

  const summary = questions.ready
    ? 'Ready to apply'
    : `${questions.answered} of ${questions.required} required answered`;

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle1" component={headingComponent} sx={{ fontWeight: 600, mb: 0.5 }}>
        Application questions
      </Typography>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
        {questions.ready && <ReadyIcon fontSize="small" color="success" />}
        <Typography variant="body2" sx={{ fontWeight: 600, color: questions.ready ? 'success.dark' : 'text.primary' }}>
          {summary}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {questions.total === 1 ? '(1 question in all)' : `(${questions.total} questions in all)`}
        </Typography>
      </Stack>
      {!questions.ready && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0, mb: 1 }}>
          Answer the missing ones in your{' '}
          <Link component={RouterLink} to="/answers">
            Answer Library
          </Link>{' '}
          and the extension can fill this form for you.
        </Typography>
      )}

      <Box component="ul" aria-label="Application questions" sx={{ listStyle: 'none', p: 0, m: 0 }}>
        {visible.map((q, index) => {
          const missing = isMissing(q);
          const kind = KIND_LABELS[q.kind];
          const notes: string[] = [];
          if (q.required) notes.push(missing ? 'Required, not answered' : 'Required');
          if (kind) notes.push(kind);
          return (
            <Box component="li" key={`${q.label}-${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.5 }}>
              <Box sx={{ display: 'flex', flexShrink: 0, mt: '2px' }}>
                {q.answered ? (
                  <AnsweredIcon fontSize="small" color="success" titleAccess="Answered" />
                ) : (
                  <UnansweredIcon fontSize="small" color={missing ? 'warning' : 'disabled'} titleAccess="Not answered" />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: missing ? 600 : 400, overflowWrap: 'anywhere' }}>
                  {q.label}
                </Typography>
                {notes.length > 0 && (
                  <Typography variant="caption" component="div" sx={{ color: missing ? 'warning.dark' : 'text.secondary' }}>
                    {notes.join(' | ')}
                  </Typography>
                )}
              </Box>
              {/* Custom wordings and attachments are answered elsewhere; only
                  the standard questions have a row in the Answer Library. */}
              {!q.answered && q.kind === 'standard' && (
                <Link component={RouterLink} to="/answers" variant="caption" sx={{ fontWeight: 600, flexShrink: 0, mt: '3px' }}>
                  Answer
                </Link>
              )}
            </Box>
          );
        })}
      </Box>
      {(hidden > 0 || showAll) && ordered.length > Math.max(MAX_SHOWN, missingCount) && (
        <Button size="small" onClick={() => setShowAll((value) => !value)} aria-expanded={showAll} sx={{ ml: -0.5 }}>
          {showAll ? 'Show fewer' : `Show ${hidden} more`}
        </Button>
      )}
    </Box>
  );
};

export default ApplicationQuestions;
